import type {
  OffsetPaginationMeta,
  CursorPaginationMeta,
} from "@/types/pagination";

export interface OffsetPaginatedResult<T> {
  data: T[];
  meta: OffsetPaginationMeta;
}

export interface CursorPaginatedResult<T> {
  data: T[];
  meta: CursorPaginationMeta;
}

export interface FilterParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

/**
 * Apply pagination to an array
 */
export function paginate<T>(
  items: T[],
  page: number = 1,
  limit: number = 10,
): OffsetPaginatedResult<T> {
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedItems = items.slice(startIndex, endIndex);
  const totalPages = Math.ceil(items.length / limit);

  return {
    data: paginatedItems,
    meta: {
      count: items.length,
      currentPage: page,
      nextPage: page < totalPages ? page + 1 : null,
      previousPage: page > 1 ? page - 1 : null,
    },
  };
}

/**
 * Apply search filter to items
 */
export function applySearch<T extends Record<string, unknown>>(
  items: T[],
  search: string | undefined,
  fields: (keyof T)[],
): T[] {
  if (!search) return items;

  const searchLower = search.toLowerCase();

  return items.filter((item) =>
    fields.some((field) => {
      const value = item[field];

      if (typeof value === "string") {
        return value.toLowerCase().includes(searchLower);
      }

      return false;
    }),
  );
}

/**
 * Apply sorting to items
 */
export function applySort<T extends Record<string, unknown>>(
  items: T[],
  sortBy: string | undefined,
  sortOrder: "asc" | "desc" = "asc",
): T[] {
  if (!sortBy) return items;

  return [...items].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];

    if (aValue === undefined || bValue === undefined) return 0;

    let comparison = 0;

    if (typeof aValue === "string" && typeof bValue === "string") {
      comparison = aValue.localeCompare(bValue);
    } else if (typeof aValue === "number" && typeof bValue === "number") {
      comparison = aValue - bValue;
    }

    return sortOrder === "desc" ? -comparison : comparison;
  });
}
